/**
 * OrgCarousel: shared presentational carousel for a set of organization cards.
 *
 * Receives a pre-fetched `organizations` array and renders it as a paginated
 * 3-up card grid with prev/next arrow buttons and dot indicators. Page state
 * is owned internally and starts at 0 on mount.
 *
 * Used by:
 *   - EcosystemOrgsBlock (CMS content block)
 *   - RecentOrgsBlock (CMS content block)
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';

import { useEcosystem } from '@/shared/contexts/EcosystemContext';
import { RILE_MARK_URL } from '@/shared/utils/brandAssets';

import './OrgCarousel.css';

const PAGE_SIZE = 3;

export default function OrgCarousel({
    organizations,
    heading,
    subtitle,
    sectionClassName,
}) {
    const { basePath } = useEcosystem();
    const [currentPage, setCurrentPage] = useState(0);

    const pageCount = Math.ceil(organizations.length / PAGE_SIZE);
    const visibleOrgs = organizations.slice(
        currentPage * PAGE_SIZE,
        (currentPage + 1) * PAGE_SIZE
    );

    return (
        <section className={`${sectionClassName} py-5`}>
            <Container>
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h2 className="text-primary mb-1">{heading}</h2>
                        <p className="text-muted mb-0">{subtitle}</p>
                    </div>
                    <Button
                        as={Link}
                        to={`${basePath}/directory`}
                        variant="outline-primary"
                        className="rounded-pill d-none d-md-inline-block"
                    >
                        View All Organizations
                    </Button>
                </div>

                <div className="position-relative">
                    {pageCount > 1 && (
                        <>
                            <Button
                                variant="light"
                                className={
                                    'position-absolute start-0 top-50 translate-middle-y ' +
                                    'rounded-circle shadow-sm d-none d-lg-flex ' +
                                    'align-items-center justify-content-center ' +
                                    'carousel-nav-btn carousel-nav-btn-prev'
                                }
                                onClick={() =>
                                    setCurrentPage((p) => Math.max(0, p - 1))
                                }
                                disabled={currentPage === 0}
                                aria-label="Previous page"
                            >
                                ‹
                            </Button>
                            <Button
                                variant="light"
                                className={
                                    'position-absolute end-0 top-50 translate-middle-y ' +
                                    'rounded-circle shadow-sm d-none d-lg-flex ' +
                                    'align-items-center justify-content-center ' +
                                    'carousel-nav-btn carousel-nav-btn-next'
                                }
                                onClick={() =>
                                    setCurrentPage((p) =>
                                        Math.min(pageCount - 1, p + 1)
                                    )
                                }
                                disabled={currentPage === pageCount - 1}
                                aria-label="Next page"
                            >
                                ›
                            </Button>
                        </>
                    )}

                    <Row className="g-4">
                        {visibleOrgs.map((org) => (
                            <Col md={4} key={org._id}>
                                <Card className="h-100 border-0 shadow-soft">
                                    <Card.Body className="p-4 d-flex flex-column">
                                        <img
                                            src={org.imageUrl || RILE_MARK_URL}
                                            alt={org.name}
                                            className="org-image mb-3"
                                            onError={(e) => {
                                                e.target.src = RILE_MARK_URL;
                                            }}
                                        />
                                        <h5 className="text-primary mb-2">
                                            {org.name}
                                        </h5>
                                        <Badge bg="secondary" className="mb-2">
                                            {org.primarySector}
                                        </Badge>
                                        <p className="text-muted small mb-3">
                                            {org.description
                                                ? org.description.length > 100
                                                    ? `${org.description.substring(0, 100)}...`
                                                    : org.description
                                                : 'No description available'}
                                        </p>
                                        {org.connectionCount > 0 && (
                                            <p className="small text-muted mb-0">
                                                🔗 {org.connectionCount}{' '}
                                                connection
                                                {org.connectionCount !== 1 &&
                                                    's'}
                                            </p>
                                        )}
                                        <div className="d-grid gap-2 mt-auto pt-3">
                                            <Button
                                                as={Link}
                                                to={`${basePath}/organizations/${org._id}`}
                                                variant="outline-primary"
                                                size="sm"
                                                className="rounded-pill"
                                            >
                                                View Details
                                            </Button>
                                            {org.website && (
                                                <Button
                                                    href={org.website}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="rounded-pill"
                                                >
                                                    Visit Website
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>

                {pageCount > 1 && (
                    <div className="d-flex justify-content-center mt-4 gap-2">
                        {Array.from({ length: pageCount }).map((_, idx) => (
                            <button
                                key={idx}
                                type="button"
                                className={`rounded-circle border-0 carousel-dot ${
                                    currentPage === idx
                                        ? 'bg-primary carousel-dot--active'
                                        : 'bg-secondary'
                                }`}
                                onClick={() => setCurrentPage(idx)}
                                aria-label={`Go to page ${idx + 1}`}
                            />
                        ))}
                    </div>
                )}

                <div className="text-center mt-4 d-md-none">
                    <Button
                        as={Link}
                        to={`${basePath}/directory`}
                        variant="outline-primary"
                        className="rounded-pill"
                    >
                        View All Organizations
                    </Button>
                </div>
            </Container>
        </section>
    );
}

OrgCarousel.propTypes = {
    organizations: PropTypes.arrayOf(PropTypes.object).isRequired,
    heading: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    sectionClassName: PropTypes.string.isRequired,
};
